import React, { useState, useMemo, useEffect } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Ring } from "@uiball/loaders";
import { updateLot, deleteLot, getMyProduct, fetchMyStoreLot } from "@/utils/services/user.service";
import {
  LayoutWrapper,
  MainArea,
  Sidebar,
  HeaderBanner,
  MainTitle,
  Subtitle,
  Card,
  CardHeader,
  SectionTitle,
  Label,
  TextInput,
  ChipButton,
  DropZone,
  HiddenFileInput,
  HierarchyButton,
  PreviewSection,
  SectionHeader,
  DataRow,
  ProgressWrapper,
  ProgressFill,
  ProductSelectWrapper,
  ProductSelectDisplay,
  ProductSelectLeft,
  ProductThumb,
  ProductSelectName,
  ProductSelectDropdown,
  ProductOption,
  LotLoadingOverlay,
} from "./PharmaLotForm.styles";

// Helper to get current logged-in user ID from localStorage
function getCurrentUserId() {
  try {
    const userData = JSON.parse(localStorage.getItem("user"));
    return (
      userData?.data?.user?.user_id ||
      userData?.data?.user?._id ||
      userData?.data?.user_id ||
      null
    );
  } catch (error) {
    console.error("Error getting user ID from localStorage in PharmaLotView:", error);
    return null;
  }
}

export default function PharmaLotView() {
  const { storeId, lotId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const initialLotFromState = location.state?.lotItem || null;
  const [storeProducts, setStoreProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingLot, setIsLoadingLot] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lotData, setLotData] = useState(null);
  const [currentUserId] = useState(getCurrentUserId);

  const [sections, setSections] = useState({
    general: true,
    production: false,
    compliance: false,
    documents: false,
  });

  const [showHierarchy, setShowHierarchy] = useState(false);
  const [activeField, setActiveField] = useState(null);

  const [form, setForm] = useState({
    lotNumber: "",
    productName: "",
    productCategory: "",
    gtin: "",
    manufacturer: "",
    productionDate: "",
    expiryDate: "",
    temperatureMin: "",
    temperatureMax: "",
  });
  console.log("🚀 ~ PharmaLotView ~ form:", form)

  const [selectedProductId, setSelectedProductId] = useState("");
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [documents, setDocuments] = useState({ quality: [], regulatory: [] });

  // Fetch store products
  useEffect(() => {
    if (!storeId) return;
    const fetchStoreProducts = async () => {
      try {
        setIsLoadingProducts(true);
        const response = await getMyProduct({
          store_id: storeId,
          limit: 200,
          pageNumber: 1,
        });
        const raw = response?.data?.products || response?.data?.data || [];
        setStoreProducts(Array.isArray(raw) ? raw : []);
      } catch (err) {
        console.error("Error fetching store products:", err);
        setStoreProducts([]);
      } finally {
        setIsLoadingProducts(false);
      }
    };
    fetchStoreProducts();
  }, [storeId]);

  // Fetch lot data
  useEffect(() => {
    if (!storeId || !lotId) return;
    const fetchLotData = async () => {
      try {
        setIsLoadingLot(true);
        let lot = initialLotFromState || null;

        // If we didn't receive the lot via route state, fall back to API fetch
        if (!lot) {
          const response = await fetchMyStoreLot({ store_id: storeId });
          console.log("🚀 ~ fetchLotData ~ response:", response);
          const lots = response?.data?.data || response?.data?.lots || [];
          lot = Array.isArray(lots) ? lots.find((l) => l._id === lotId) : null;
          console.log("🚀 ~ fetchLotData ~ lot:", lot)
        }
        
        if (!lot) {
          toast.error("Lot not found");
          history.push(`/storedetail/${storeId}`);
          return;
        }

        setLotData(lot);

        // Populate form with lot data
        const production = lot.production?.[0] || {};
        const compliance = lot.compliance?.[0] || {};

        setForm({
          lotNumber: lot.lot_number || "",
          // Default product name/category from lot metadata passed via state (if any)
          productName: lot.title || lot.productName || "",
          productCategory: lot.productCategoryLabel || lot.productCategory || "",
          gtin: lot.global_trade_item_number || "",
          manufacturer: production.manufacturing_batch_number || "",
          productionDate: production.production_date
            ? new Date(production.production_date).toISOString().split("T")[0]
            : "",
          expiryDate: production.expiry_date
            ? new Date(production.expiry_date).toISOString().split("T")[0]
            : "",
          temperatureMin: compliance.minimum_validated_storage_temperature?.toString() || "",
          temperatureMax: compliance.maximum_validated_storage_temperature?.toString() || "",
        });

        setSelectedProductId(lot.product_id || "");

        // Set documents (if they exist as URLs or file names)
        const docUrls = lot.documents || [];
        setDocuments({
          quality: docUrls.filter((url) => typeof url === "string") || [],
          regulatory: [],
        });
      } catch (err) {
        console.error("Error fetching lot data:", err);
        toast.error("Failed to load lot data");
        history.push(`/storedetail/${storeId}`);
      } finally {
        setIsLoadingLot(false);
      }
    };
    fetchLotData();
  }, [storeId, lotId, history, initialLotFromState]);

  // Update product name and category when product is selected
  useEffect(() => {
    if (selectedProductId && storeProducts.length > 0) {
      const product = storeProducts.find((p) => String(p._id) === String(selectedProductId));
      if (product) {
        const subCategoryName =
          product.category_detail &&
          Array.isArray(product.category_detail) &&
          product.category_detail[0] &&
          Array.isArray(product.category_detail[0].sub_categories) &&
          product.category_detail[0].sub_categories[0]
            ? product.category_detail[0].sub_categories[0].sub_category_name
            : null;

        setForm((prev) => ({
          ...prev,
          productName: product.title || product.name || "",
          productCategory:
            subCategoryName ||
            product.category_name?.[0]?.category_name ||
            product.category_name ||
            prev.productCategory,
          gtin: product.global_trade_item_number || product.gtin || prev.gtin,
        }));
      }
    }
  }, [selectedProductId, storeProducts]);

  const toggle = (key) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...form, [name]: value };

    if (name === "productCategory") {
      const ranges = {
        "Room Controlled": { min: "15", max: "25" },
        "Refrigerated": { min: "2", max: "8" },
        "Frozen": { min: "-25", max: "-15" },
        "Deep Frozen": { min: "-80", max: "-60" },
      };
      if (ranges[value]) {
        updated.temperatureMin = ranges[value].min;
        updated.temperatureMax = ranges[value].max;
      }
    }
    setForm(updated);
  };

  const buildUpdatePayload = () => {
    const matchedProduct =
      storeProducts?.find((p) => String(p._id) === String(selectedProductId)) ||
      storeProducts?.[0];

    const category_id =
      matchedProduct?.category_id ||
      matchedProduct?.category?._id ||
      matchedProduct?.category_name?._id ||
      lotData?.category_id ||
      null;

    const compliance = [
      {
        minimum_validated_storage_temperature:
          form.temperatureMin !== ""
            ? Number(form.temperatureMin)
            : undefined,
        maximum_validated_storage_temperature:
          form.temperatureMax !== ""
            ? Number(form.temperatureMax)
            : undefined,
      },
    ];

    const production = [
      {
        manufacturing_batch_number: form.manufacturer,
        production_date: form.productionDate
          ? new Date(form.productionDate).toISOString()
          : undefined,
        expiry_date: form.expiryDate
          ? new Date(form.expiryDate).toISOString()
          : undefined,
      },
    ];

    return {
      record_id: lotId,
      lot_number: form.lotNumber,
      category_id,
      global_trade_item_number: form.gtin,
      compliance,
      production,
    };
  };

  const handleUpdateLot = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const payload = buildUpdatePayload();
      await updateLot(payload);
      toast.success("Lot updated successfully");
      setIsEditing(false);
      // Refresh lot data
      const response = await fetchMyStoreLot({ store_id: storeId });
      const lots = response?.data?.data || response?.data?.lots || [];
      const updatedLot = Array.isArray(lots) ? lots.find((l) => l._id === lotId) : null;
      if (updatedLot) {
        setLotData(updatedLot);
      }
    } catch (err) {
      console.error("Error updating lot", err);
      toast.error(err?.response?.data?.message || "Failed to update lot");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLot = async () => {
    if (isDeleting) return;
    try {
      setIsDeleting(true);
      await deleteLot({ record_id: lotId });
      toast.success("Lot deleted successfully");
      history.push(`/storedetail/${storeId}`);
    } catch (err) {
      console.error("Error deleting lot", err);
      toast.error(err?.response?.data?.message || "Failed to delete lot");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const isOwner = useMemo(() => {
    if (!lotData || !currentUserId) return false;
    const lotOwnerId = lotData.user_id || lotData.userId;
    return lotOwnerId && String(lotOwnerId) === String(currentUserId);
  }, [lotData, currentUserId]);

  const completion = useMemo(() => {
    const total = 5;
    let score = 0;
    if (form.lotNumber) score++;
    if (form.productName || selectedProductId) score++;
    if (form.productionDate) score++;
    if (form.expiryDate) score++;
    if (documents.quality.length > 0) score++;
    return Math.round((score / total) * 100);
  }, [form, documents, selectedProductId]);

  if (isLoadingLot) {
    return (
      <LotLoadingOverlay>
        <Ring size={65} color="#5ce9ff" />
      </LotLoadingOverlay>
    );
  }

  if (!lotData) {
    return null;
  }

  const selectedProduct = storeProducts.find(
    (p) => String(p._id) === String(selectedProductId)
  );

  return (
    <LayoutWrapper>
      <MainArea>
        <HeaderBanner>
          <MainTitle>Pharmacrowd  - Master Lot</MainTitle>
          <Subtitle>Lot View & Edit</Subtitle>
        </HeaderBanner>

        {/* Action Buttons - visible only to lot owner */}
        {isOwner && (
        <div style={{ display: "flex", gap: "12px", marginBottom: "30px" }}>
          {!isEditing ? (
            <>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                style={{
                  padding: "12px 24px",
                  borderRadius: "10px",
                  background: "linear-gradient(90deg, #00f7d2, #5ce9ff)",
                  color: "#000",
                  border: "none",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting}
                style={{
                  padding: "12px 24px",
                  borderRadius: "10px",
                  background: isDeleting ? "rgba(239, 68, 68, 0.5)" : "rgba(239, 68, 68, 0.9)",
                  color: "#fff",
                  border: "none",
                  fontWeight: 600,
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  fontSize: "14px",
                }}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleUpdateLot}
                disabled={isSubmitting}
                style={{
                  padding: "12px 24px",
                  borderRadius: "10px",
                  background: isSubmitting
                    ? "rgba(0, 247, 210, 0.5)"
                    : "linear-gradient(90deg, #00f7d2, #5ce9ff)",
                  color: "#000",
                  border: "none",
                  fontWeight: 600,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  fontSize: "14px",
                }}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  // Reset form to original lot data
                  const production = lotData.production?.[0] || {};
                  const compliance = lotData.compliance?.[0] || {};
                  setForm({
                    lotNumber: lotData.lot_number || "",
                    productName: "",
                    productCategory: "",
                    gtin: lotData.global_trade_item_number || "",
                    manufacturer: production.manufacturing_batch_number || "",
                    productionDate: production.production_date
                      ? new Date(production.production_date).toISOString().split("T")[0]
                      : "",
                    expiryDate: production.expiry_date
                      ? new Date(production.expiry_date).toISOString().split("T")[0]
                      : "",
                    temperatureMin: compliance.minimum_validated_storage_temperature?.toString() || "",
                    temperatureMax: compliance.maximum_validated_storage_temperature?.toString() || "",
                  });
                }}
                disabled={isSubmitting}
                style={{
                  padding: "12px 24px",
                  borderRadius: "10px",
                  background: "rgba(255, 255, 255, 0.1)",
                  color: "#fff",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  fontWeight: 600,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
        )}

        <Section
          title="General"
          openKey="general"
          sections={sections}
          toggle={toggle}
        >
          <Field
            label="Lot Number"
            help="Unique batch identifier assigned by the manufacturer."
            name="lotNumber"
            // Use text so alphanumeric lot numbers like '234aas' display correctly
            type="text"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
            disabled={!isEditing}
          />
          <div style={{ marginBottom: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Label>Product Name</Label>
            </div>
            <ProductSelectWrapper style={{ marginTop: 6 }}>
              {(() => {
                const selected = selectedProduct || null;
                const thumb =
                  selected?.product_image ||
                  selected?.pictures?.[0] ||
                  selected?.image ||
                  selected?.coverImage ||
                  "";

                return (
                  <>
                    <ProductSelectDisplay
                      type="button"
                      onClick={() =>
                        isEditing &&
                        !isLoadingProducts &&
                        setProductDropdownOpen((open) => !open)
                      }
                      style={{
                        cursor: isEditing ? "pointer" : "default",
                        opacity: isEditing ? 1 : 0.7,
                      }}
                    >
                      <ProductSelectLeft>
                        {thumb ? (
                          <ProductThumb style={{ backgroundImage: `url(${thumb})` }} />
                        ) : (
                          <ProductThumb />
                        )}
                        <ProductSelectName>
                          {isLoadingProducts
                            ? "Loading products..."
                            : selected
                            ? selected.title || selected.name || "Untitled"
                            : "Select product"}
                        </ProductSelectName>
                      </ProductSelectLeft>
                      {isEditing && (
                        <span style={{ fontSize: 10, color: "#64748b" }}>
                          {productDropdownOpen ? "▲" : "▼"}
                        </span>
                      )}
                    </ProductSelectDisplay>

                    {isEditing && productDropdownOpen && storeProducts.length > 0 && (
                      <ProductSelectDropdown>
                        {storeProducts.map((p) => {
                          const img =
                            p.product_image || p.pictures?.[0] || p.image || p.coverImage || "";
                          return (
                            <ProductOption
                              key={p._id}
                              type="button"
                              onClick={() => {
                                setSelectedProductId(p._id);
                                setForm((prev) => ({
                                  ...prev,
                                  productName: p.title || p.name || "",
                                  gtin: p.global_trade_item_number || p.gtin || prev.gtin,
                                  productCategory:
                                    p.category_name?.[0]?.category_name ||
                                    p.category_name ||
                                    prev.productCategory,
                                }));
                                setProductDropdownOpen(false);
                              }}
                            >
                              {img && (
                                <ProductThumb
                                  style={{
                                    marginRight: 10,
                                    backgroundImage: `url(${img})`,
                                  }}
                                />
                              )}
                              <ProductSelectName>
                                {p.title || p.name || "Untitled"}
                              </ProductSelectName>
                            </ProductOption>
                          );
                        })}
                      </ProductSelectDropdown>
                    )}
                  </>
                );
              })()}
            </ProductSelectWrapper>
          </div>

          <div style={{ marginBottom: 22 }}>
            <Label>Product Category</Label>
            <TextInput
              type="text"
              value={form.productCategory}
              readOnly
              disabled
              style={{ marginTop: 6, opacity: 0.7 }}
            />
          </div>

          <Field
            label="GTIN"
            help="Global Trade Item Number used for product identification."
            name="gtin"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
            disabled={!isEditing}
          />
        </Section>

        <Section
          title="Production"
          openKey="production"
          sections={sections}
          toggle={toggle}
        >
          <Field
            label="Manufacturer"
            help="Legal entity responsible for manufacturing the batch."
            name="manufacturer"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
            disabled={!isEditing}
          />
          <Field
            label="Production Date"
            help="Date on which the batch was produced."
            name="productionDate"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
            disabled={!isEditing}
          />
          <Field
            label="Expiry Date"
            help="Date after which the product must not be used."
            name="expiryDate"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
            disabled={!isEditing}
          />
        </Section>

        <Section
          title="Compliance"
          openKey="compliance"
          sections={sections}
          toggle={toggle}
        >
          <Field
            label="Temp Min"
            help="Minimum validated storage temperature in °C."
            name="temperatureMin"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
            disabled={!isEditing}
          />
          <Field
            label="Temp Max"
            help="Maximum validated storage temperature in °C."
            name="temperatureMax"
            form={form}
            activeField={activeField}
            setActiveField={setActiveField}
            handleChange={handleChange}
            disabled={!isEditing}
          />
        </Section>

        <Section
          title="Documents"
          openKey="documents"
          sections={sections}
          toggle={toggle}
        >
          <div style={{ marginTop: 20 }}>
            {documents.quality.length === 0 && documents.regulatory.length === 0 ? (
              <div style={{ fontSize: 11, color: "#64748b" }}>No documents</div>
            ) : (
              <>
                {documents.quality.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, color: "#5ce9ff", marginBottom: 6 }}>
                      QUALITY
                    </div>
                    {documents.quality.map((doc, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 12,
                          marginBottom: 4,
                        }}
                      >
                        <span>
                          • {typeof doc === "string" ? doc : doc.name || "Document"}
                        </span>
                        {typeof doc === "string" && (
                          <a
                            href={doc}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#5ce9ff", textDecoration: "none" }}
                          >
                            View
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {documents.regulatory.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, color: "#5ce9ff", marginBottom: 6 }}>
                      REGULATORY
                    </div>
                    {documents.regulatory.map((doc, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 12,
                          marginBottom: 4,
                        }}
                      >
                        <span>
                          • {typeof doc === "string" ? doc : doc.name || "Document"}
                        </span>
                        {typeof doc === "string" && (
                          <a
                            href={doc}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#5ce9ff", textDecoration: "none" }}
                          >
                            View
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </Section>
      </MainArea>

      {/* ================= RIGHT SIDEBAR ================= */}
      <Sidebar>
        <PreviewSection>
          <SectionHeader>Lot Data Preview</SectionHeader>
          <DataRow>
            <span>Lot</span>
            <strong>{form.lotNumber || "—"}</strong>
          </DataRow>
          <DataRow>
            <span>Product</span>
            <strong>{form.productName || selectedProduct?.title || "—"}</strong>
          </DataRow>
          <DataRow>
            <span>Manufacturer</span>
            <strong>{form.manufacturer || "—"}</strong>
          </DataRow>
          <DataRow>
            <span>Documents</span>
            <strong>
              {documents.quality.length + documents.regulatory.length}
            </strong>
          </DataRow>
        </PreviewSection>

        <ProgressWrapper>
          <ProgressFill style={{ width: `${completion}%` }} />
        </ProgressWrapper>
        <div style={{ marginTop: 8, color: "#94a3b8" }}>{completion}% Complete</div>

        <HierarchyButton
          type="button"
          style={{
            width: "100%",
            marginTop: 20,
          }}
          onClick={() => setShowHierarchy(true)}
        >
          Open Supply Chain Hierarchy
        </HierarchyButton>
      </Sidebar>

      {/* ================= SLIDE HIERARCHY SIDEBAR ================= */}
      {showHierarchy && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "flex-end",
            zIndex: 1000,
          }}
          onClick={() => setShowHierarchy(false)}
        >
          <div
            style={{
              width: 420,
              height: "100vh",
              background: "#111522",
              padding: 30,
              boxShadow: "-10px 0 40px rgba(0,0,0,0.6)",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                paddingBottom: 10,
              }}
            >
              <div style={{ fontWeight: 600 }}>Supply Chain Hierarchy</div>
              <div
                style={{ cursor: "pointer" }}
                onClick={() => setShowHierarchy(false)}
              >
                ✕
              </div>
            </div>
            <div style={{ marginTop: 30 }}>
              <HierarchyTree form={form} />
            </div>
          </div>
        </div>
      )}

      {/* ================= DELETE CONFIRMATION MODAL ================= */}
      {showDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            style={{
              background: "#111522",
              borderRadius: "16px",
              padding: "30px",
              maxWidth: "500px",
              width: "90%",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.6)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                fontSize: "20px",
                fontWeight: 600,
                marginBottom: "16px",
                color: "#fff",
              }}
            >
              Delete Lot
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.7)",
                marginBottom: "24px",
                lineHeight: "1.5",
              }}
            >
              Are you sure you want to delete this lot? This action cannot be undone.
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  background: "rgba(255, 255, 255, 0.1)",
                  color: "#fff",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  fontWeight: 600,
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  opacity: isDeleting ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteLot}
                disabled={isDeleting}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  background: isDeleting
                    ? "rgba(239, 68, 68, 0.5)"
                    : "rgba(239, 68, 68, 0.9)",
                  color: "#fff",
                  border: "none",
                  fontWeight: 600,
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  fontSize: "14px",
                }}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isSubmitting && (
        <LotLoadingOverlay>
          <Ring size={65} color="#5ce9ff" />
        </LotLoadingOverlay>
      )}
    </LayoutWrapper>
  );
}

// ================= COMPONENTS =================

const Section = ({ title, openKey, children, sections, toggle }) => (
  <Card>
    <CardHeader onClick={() => toggle(openKey)}>
      <SectionTitle>{title}</SectionTitle>
      <span style={{ color: "#00f7d2" }}>{sections[openKey] ? "−" : "+"}</span>
    </CardHeader>
    {sections[openKey] && <div style={{ marginTop: 20 }}>{children}</div>}
  </Card>
);

const Field = ({
  label,
  help,
  name,
  type = "text",
  form,
  activeField,
  setActiveField,
  handleChange,
  disabled = false,
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const value = form[name];

  const getSuggestion = () => {
    if (!value) return null;
    if (name === "expiryDate" && form.productionDate) {
      if (new Date(value) <= new Date(form.productionDate)) {
        return "Expiry date should be later than production date.";
      }
    }
    if (name === "temperatureMin" && form.temperatureMax) {
      if (Number(value) >= Number(form.temperatureMax)) {
        return "Min temperature should be lower than Max temperature.";
      }
    }
    if (name === "temperatureMax" && form.temperatureMin) {
      if (Number(value) <= Number(form.temperatureMin)) {
        return "Max temperature should be higher than Min temperature.";
      }
    }
    return null;
  };

  const suggestion = getSuggestion();

  return (
    <div style={{ marginBottom: 22, position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Label>{label}</Label>
        {help && (
          <span
            onClick={() => setShowHelp(!showHelp)}
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: showHelp ? "#00f7d2" : "rgba(0,247,210,0.2)",
              color: showHelp ? "#000" : "#00f7d2",
              fontSize: 11,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            i
          </span>
        )}
      </div>
      {showHelp && help && (
        <div
          style={{
            marginTop: 8,
            background: "#0f172a",
            border: "1px solid rgba(0,247,210,0.3)",
            padding: "8px 10px",
            borderRadius: 8,
            fontSize: 11,
            color: "#cbd5e1",
            boxShadow: "0 0 15px rgba(0,247,210,0.2)",
          }}
        >
          {help}
        </div>
      )}
      <TextInput
        type={type}
        name={name}
        value={value}
        onFocus={() => setActiveField(name)}
        onBlur={() => setActiveField(null)}
        onChange={handleChange}
        disabled={disabled}
        style={{
          marginTop: 6,
          border: suggestion
            ? "1px solid #ff6b6b"
            : activeField === name
            ? "1px solid #00f7d2"
            : "1px solid #1f2937",
          boxShadow: suggestion
            ? "0 0 10px rgba(255,107,107,0.5)"
            : activeField === name
            ? "0 0 10px rgba(0,247,210,0.6)"
            : "none",
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? "not-allowed" : "text",
        }}
      />
      {suggestion && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#ff6b6b" }}>
          {suggestion}
        </div>
      )}
    </div>
  );
};

const HierarchyTree = ({ form }) => {
  const generateHash = (val) => {
    try {
      return btoa(val).substring(0, 10);
    } catch {
      return "—";
    }
  };

  const rootQty = 0;
  const treeData = {
    id: "root",
    label: "MasterLot",
    lot: form.lotNumber || "N/A",
    qty: rootQty,
    status: "Minted",
    hash: generateHash(form.lotNumber + rootQty),
    children: [
      {
        id: "A",
        label: "Distributor A",
        lot: form.lotNumber ? form.lotNumber + "-A" : "N/A",
        qty: Math.floor(rootQty / 2),
        status: "Transferred",
        hash: generateHash("A" + form.lotNumber),
        children: [
          {
            id: "A1",
            label: "Pharmacy A1",
            lot: form.lotNumber ? form.lotNumber + "-A1" : "N/A",
            qty: Math.floor(rootQty / 4),
            status: "Open",
            hash: generateHash("A1" + form.lotNumber),
          },
          {
            id: "A2",
            label: "Pharmacy A2",
            lot: form.lotNumber ? form.lotNumber + "-A2" : "N/A",
            qty: Math.floor(rootQty / 4),
            status: "Open",
            hash: generateHash("A2" + form.lotNumber),
          },
        ],
      },
      {
        id: "B",
        label: "Distributor B",
        lot: form.lotNumber ? form.lotNumber + "-B" : "N/A",
        qty: Math.floor(rootQty / 2),
        status: "Transferred",
        hash: generateHash("B" + form.lotNumber),
        children: [
          {
            id: "B1",
            label: "Pharmacy B1",
            lot: form.lotNumber ? form.lotNumber + "-B1" : "N/A",
            qty: Math.floor(rootQty / 2),
            status: "Open",
            hash: generateHash("B1" + form.lotNumber),
          },
        ],
      },
    ],
  };

  const Node = ({ node, level = 0 }) => (
    <div style={{ marginLeft: level * 20, marginTop: 20, position: "relative" }}>
      {level !== 0 && (
        <svg
          width="20"
          height="20"
          style={{ position: "absolute", left: -20, top: 10 }}
        >
          <line
            x1="20"
            y1="0"
            x2="0"
            y2="10"
            stroke="#00f7d2"
            strokeWidth="1.5"
          />
        </svg>
      )}
      <div
        style={{
          padding: "12px 16px",
          borderRadius: 12,
          background: "#111c2a",
          border: "1px solid rgba(0,247,210,0.4)",
          boxShadow: "0 0 12px rgba(0,247,210,0.3)",
          transition: "all 0.4s ease",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13 }}>{node.label}</div>
        <div style={{ fontSize: 11, color: "#5ce9ff" }}>Lot: {node.lot}</div>
        <div style={{ fontSize: 11 }}>Qty: {node.qty}</div>
        <div style={{ fontSize: 10, color: "#94a3b8" }}>Hash: {node.hash}</div>
        <div
          style={{
            marginTop: 6,
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: 8,
            fontSize: 10,
            background:
              node.status === "Minted"
                ? "rgba(0,247,210,0.2)"
                : node.status === "Transferred"
                ? "rgba(92,233,255,0.2)"
                : "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          {node.status}
        </div>
      </div>
      {node.children &&
        node.children.map((child) => (
          <Node key={child.id} node={child} level={level + 1} />
        ))}
    </div>
  );

  return (
    <div style={{ animation: "slideIn 0.4s ease" }}>
      <Node node={treeData} />
    </div>
  );
};
